// FILE: apps/admin/src/pages/Settings.tsx
// VERSION: 3.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Streamlined Admin System Settings page for payment-matching, polling timers, Tamagotchi, and operational runtime parameters, while re-exporting buildPricingPreviewRows from Pricing.tsx for backwards compatibility.
//   SCOPE: Settings React component and backwards-compatible buildPricingPreviewRows / preview types exports.
//   DEPENDS: ../api.js, ../components/Icons.js, ../toast.js, ./Pricing.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   PremiumPreviewRow - Backwards-compatible re-export of Telegram Premium preview row type from Pricing
//   PricingPreviewResult - Backwards-compatible re-export of pricing preview result type from Pricing
//   Settings - Admin System & Operational Settings page component with group tabs and link to Pricing
//   StarPreviewRow - Backwards-compatible re-export of Telegram Stars preview row type from Pricing
//   buildPricingPreviewRows - Backwards-compatible re-export of pure Fragment retail pricing preview helper from Pricing
// END_MODULE_MAP

import { type AdminPricingPreviewDto } from '@brainrotstore/api-contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import { useToast } from '../toast.js';
import {
  buildPricingPreviewRows as computePricingPreviewRows,
  type PremiumPreviewRow as PricingPremiumPreviewRow,
  type PricingPreviewResult as PricingPreviewComputedResult,
  type StarPreviewRow as PricingStarPreviewRow,
} from './Pricing.js';

export type StarPreviewRow = PricingStarPreviewRow;
export type PremiumPreviewRow = PricingPremiumPreviewRow;
export type PricingPreviewResult = PricingPreviewComputedResult;

interface Field {
  key: string;
  label: string;
  group: string;
  type: 'number' | 'number[]';
  hint?: string;
  default: number | number[];
  value: number | number[];
}

const MOVED_TO_PRICING_KEYS = new Set([
  'STARS_MARKUP_MULTIPLIER',
  'PREMIUM_MARKUP_MULTIPLIER',
  'PRICING_ALERT_MIN_DELTA_RUB',
  'STAR_PACKS',
  'MIN_STARS',
  'MAX_STARS',
  'STARS_MIN',
  'STARS_MAX',
]);

function formatGroupTabTitle(group: string): string {
  // START_BLOCK_FORMAT_GROUP_TAB_TITLE
  if (group === 'Тайминги') return 'Тайминги и СБП-матчинг';
  if (group === 'Цены') return 'Партнёрские и общие ставки';
  return group;
  // END_BLOCK_FORMAT_GROUP_TAB_TITLE
}

// START_CONTRACT: buildPricingPreviewRows
//   PURPOSE: Delegate live Stars and Premium retail pricing preview calculation to Pricing.tsx for backwards compatibility.
//   INPUTS: preview: AdminPricingPreviewDto, starsDraft?: string, premiumDraft?: string
//   OUTPUTS: PricingPreviewResult
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: buildPricingPreviewRows
export function buildPricingPreviewRows(
  preview: AdminPricingPreviewDto,
  starsDraft?: string,
  premiumDraft?: string,
): PricingPreviewResult {
  // START_BLOCK_SETTINGS_REEXPORT_PRICING_PREVIEW
  return computePricingPreviewRows(preview, starsDraft, premiumDraft);
  // END_BLOCK_SETTINGS_REEXPORT_PRICING_PREVIEW
}

// START_CONTRACT: Settings
//   PURPOSE: Render streamlined System & Operational Settings editor with horizontal group tabs and navigation banner to /pricing.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries and mutates /admin/settings
//   LINKS: M-ADMIN
// END_CONTRACT: Settings
export function Settings() {
  // START_BLOCK_SETTINGS
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Field[]>('/admin/settings'),
  });

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const d: Record<string, string> = {};
    for (const f of data) {
      d[f.key] = Array.isArray(f.value) ? f.value.join(', ') : String(f.value);
    }
    setDraft(d);
  }, [data]);

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: number | number[] }) =>
      api.put<{ ok: boolean; reason?: string }>('/admin/settings', { key, value }),
    onSuccess: (r) => {
      toast(r.ok ? 'Параметр сохранён' : (r.reason ?? 'Не удалось сохранить'));
      if (r.ok) {
        void qc.invalidateQueries({ queryKey: ['settings'] });
      }
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сохранения'),
  });

  const submit = (f: Field) => {
    const raw = draft[f.key] ?? '';
    if (f.type === 'number[]') {
      const arr = raw
        .split(',')
        .map((x) => Number(x.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (!arr.length) return toast('Введите положительные числа через запятую');
      save.mutate({ key: f.key, value: arr });
    } else {
      const n = Number(raw.replace(',', '.').trim());
      if (!Number.isFinite(n) || n < 0) return toast('Введите неотрицательное число');
      save.mutate({ key: f.key, value: n });
    }
  };

  if (isLoading || !data) return <div className="muted">Загрузка системных параметров…</div>;

  const systemFields = data.filter(
    (f) => !MOVED_TO_PRICING_KEYS.has(f.key) && f.group !== 'Витрина',
  );

  const rawGroups = [...new Set(systemFields.map((f) => f.group))];
  const groups = rawGroups.sort((a, b) => {
    if (a === 'Тайминги') return -1;
    if (b === 'Тайминги') return 1;
    return a.localeCompare(b, 'ru');
  });

  const activeGroup =
    selectedGroup && groups.includes(selectedGroup) ? selectedGroup : (groups[0] ?? null);

  const visibleFields =
    groups.length > 1 && activeGroup
      ? systemFields.filter((f) => f.group === activeGroup)
      : systemFields;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Системные настройки</h1>
          <div className="page-subtitle">
            Операционные параметры платформы, тайминги СБП-матчинга Т-Банка, опроса сессий и механик без перезапуска сервера
          </div>
        </div>
      </div>

      <div
        className="card"
        style={{
          marginBottom: 18,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          background: 'linear-gradient(135deg, rgba(130, 148, 255, 0.11), var(--bg-1) 75%)',
          borderColor: 'rgba(130, 148, 255, 0.28)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: '1 1 260px', minWidth: 0 }}>
          <div className="mp-kpi-icon purple" style={{ width: 38, height: 38 }}>
            <AdminIcon name="tag" size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14.5 }}>
              Наценки Stars / Premium, курс Fragment и ступени за оборот вынесены в отдельный раздел
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>
              Управляйте множителями X, комиссиями в %, прогрессивной шкалой скидок и пакетами витрины в разделе «Цены и тарифы».
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn sm ghost"
            onClick={() => navigate('/pricing?tab=tiers')}
          >
            Ступени за оборот
          </button>
          <button
            type="button"
            className="btn sm primary"
            onClick={() => navigate('/pricing')}
          >
            <span>Перейти в «Цены и тарифы» →</span>
          </button>
        </div>
      </div>

      {groups.length > 1 && (
        <div className="tabs">
          {groups.map((g) => {
            const countInGroup = systemFields.filter((f) => f.group === g).length;
            return (
              <button
                key={g}
                type="button"
                className={`tab-btn ${activeGroup === g ? 'active' : ''}`}
                onClick={() => setSelectedGroup(g)}
              >
                {formatGroupTabTitle(g)} ({countInGroup})
              </button>
            );
          })}
        </div>
      )}

      <div className="card">
        {visibleFields.length === 0 ? (
          <div className="muted">Нет системных параметров для отображения.</div>
        ) : (
          visibleFields.map((f, idx) => {
            const def = Array.isArray(f.default) ? f.default.join(', ') : String(f.default);
            const cur = Array.isArray(f.value) ? f.value.join(', ') : String(f.value);
            const changed = (draft[f.key] ?? '').trim() !== cur.trim();
            return (
              <div
                key={f.key}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 14,
                  padding: '13px 0',
                  borderTop: idx > 0 ? '1px solid var(--hair)' : 'none',
                }}
              >
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{f.label}</div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>
                    {f.hint ? `${f.hint} · ` : ''}ключ: <span className="mono">{f.key}</span> · по умолчанию:{' '}
                    <span className="mono">{def}</span>
                  </div>
                </div>
                <div className="row" style={{ gap: 10, flex: '1 1 auto', justifyContent: 'flex-end' }}>
                  <input
                    type={f.type === 'number' ? 'number' : 'text'}
                    className="mono"
                    style={{ width: f.type === 'number' ? 130 : 200, maxWidth: '100%', flex: '1 1 auto', minWidth: 0 }}
                    aria-label={f.label}
                    value={draft[f.key] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  />
                  <button
                    type="button"
                    className="btn sm primary"
                    disabled={!changed || save.isPending}
                    onClick={() => submit(f)}
                  >
                    <AdminIcon name="check" size={13} />
                    <span>Сохранить</span>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
  // END_BLOCK_SETTINGS
}
